import { useCallback, useMemo, type ReactNode } from "react";
import { defineMessages, useIntl } from "react-intl";

import Link from "common/core/link";
import { Substyle } from "common/core/typography";

import { ChartsSection } from "./shared/charts_section";
import { EmptyChart } from "./shared/empty_chart";
import { NumericalChart } from "./shared/numerical_chart";
import type { NotarizeCountScalarHash } from "./shared/utils";

const MESSAGES = defineMessages({
  chartsSectionHeading: {
    id: "ba922ea8-49e2-4483-a205-d14d1a9b9c38",
    defaultMessage: "Proof value metrics",
  },
  viewingAllTime: {
    id: "9ed3a619-02e5-493f-9729-169baa226ff8",
    defaultMessage: "All time transactions",
  },
  totalCostSavings: {
    id: "38a9addf-52ff-4088-b953-def5723f9ff1",
    defaultMessage: "Total cost savings",
  },
  emptyCostSavings: {
    id: "51b54a7a-a521-423a-9647-dd54b76dcb9b",
    defaultMessage: "No notarization cost savings yet",
  },
  emptyCostSavingsDescription: {
    id: "0d8b6d5f-a8f3-43ad-b1a7-4718616a68e9",
    defaultMessage: "Cost savings will display here.",
  },
  totalTimeSavings: {
    id: "41bf2cbb-5dcf-4031-8d0e-42b6d1ccc021",
    defaultMessage: "Total time savings",
  },
  timeFigureLabel: {
    id: "d650b349-b64a-4485-91ca-e50d3a18366b",
    defaultMessage: "minutes in meeting",
  },
  emptyTimeSavings: {
    id: "31a674ac-07ec-43ba-905d-a796b3688fa3",
    defaultMessage: "No notarization time savings yet",
  },
  emptyTimeSavingsDescription: {
    id: "f4848b75-ecde-466b-9f74-2024c60f7da8",
    defaultMessage: "Time savings will display here.",
  },
});

function IDCSubDetail() {
  const intl = useIntl();
  return (
    <Substyle size="small" textColor="subtle">
      {intl.formatMessage(
        {
          id: "d1c3b8f0-2e4a-4c5b-9f6d-7c8e9f0a1b2c",
          defaultMessage: "Based on <a>IDC report</a>",
        },
        {
          a: (linkContent: ReactNode) => (
            <Link href="https://lp.proof.com/hubfs/Behind the Metric IDC.pdf" underlined={false}>
              {linkContent}
            </Link>
          ),
        },
      )}
    </Substyle>
  );
}

export function ValueSection({
  notarizationCount,
}: {
  notarizationCount: NotarizeCountScalarHash[];
}) {
  const intl = useIntl();

  const completedCount = useMemo(
    () => notarizationCount.reduce((sum, item) => sum + (item.count ? Number(item.count) : 0), 0),
    [notarizationCount],
  );

  const formatLargeNumber = useCallback(
    (value: bigint, isCurrency = false) => {
      const formatOptions: Intl.NumberFormatOptions = {
        maximumFractionDigits: 0,
        notation: value >= 1_000_000n ? "compact" : "standard",
      };

      if (isCurrency) {
        formatOptions.style = "currency";
        formatOptions.currency = "USD";
      }

      return Intl.NumberFormat(intl.locale, formatOptions).format(Number(value));
    },
    [intl.locale],
  );

  const formattedCostSavings = useMemo(() => {
    const costPerTransaction = 46n; // @dev magic number 46 is from cost savings as calculated by IDC report
    const totalCost = BigInt(completedCount) * costPerTransaction;
    return formatLargeNumber(totalCost, true);
  }, [completedCount, formatLargeNumber]);

  const formattedTimeSavings = useMemo(() => {
    const timePerTransaction = 42n; // @dev magic number 42 is from time savings as calculated by IDC report
    const totalTime = BigInt(completedCount) * timePerTransaction;
    return formatLargeNumber(totalTime);
  }, [completedCount, formatLargeNumber]);

  return (
    <ChartsSection
      heading={intl.formatMessage(MESSAGES.chartsSectionHeading)}
      description={intl.formatMessage(MESSAGES.viewingAllTime)}
      firstChart={
        !completedCount ? (
          <EmptyChart
            heading={intl.formatMessage(MESSAGES.emptyCostSavings)}
            description={intl.formatMessage(MESSAGES.emptyCostSavingsDescription)}
          />
        ) : (
          <NumericalChart
            number={formattedCostSavings}
            detail={intl.formatMessage(MESSAGES.totalCostSavings)}
            subDetail={<IDCSubDetail />}
          />
        )
      }
      secondChart={
        !completedCount ? (
          <EmptyChart
            heading={intl.formatMessage(MESSAGES.emptyTimeSavings)}
            description={intl.formatMessage(MESSAGES.emptyTimeSavingsDescription)}
          />
        ) : (
          <NumericalChart
            number={formattedTimeSavings}
            detail={intl.formatMessage(MESSAGES.totalTimeSavings)}
            figureLabel={intl.formatMessage(MESSAGES.timeFigureLabel)}
            subDetail={<IDCSubDetail />}
          />
        )
      }
    />
  );
}
